import { needsAccount } from './gate.jsx';
/* ══════════════════════════════════════════════════════════════════════════
 * ONE SHARE CONTROL, EVERYWHERE WORTH SHARING — the React side.
 *
 * ⚠️ THIS IS A PORT OF `Eleater Prototype/eleat_share.js`, NOT A NEW DESIGN.
 * Every string, every icon path and every rule below is quoted from that file.
 * Nothing about sharing was invented here (RULE 4), and nothing about it
 * reached the React app until 26 September 2026 — `navigator.share`,
 * `EleatShare` and the word "Share" appeared in **zero** files under
 * `web/src`, while the locked screens carry a share control in four places:
 *   · `08a_Saved.html`      — `.sharebtn` under every saved card
 *   · `11_Restaurant.html`  — `#shareVenue` in the hero, beside Save
 *   · `11_Restaurant.html`  — `#fvShare` on a Feed post
 *   · `08b_Reservations.html` — on a reservation row
 *
 * ── AL'S RULES, FROM THAT FILE'S OWN HEADER ────────────────────────────────
 *
 * Al, 29 August 2026: *"dont you think we should allow eleaters globally to
 * share via whatsapp and email. i will not for now set up sms. too expensive
 * and not necessary. in the future we can perhaps set up a process where
 * eleaters can send it within eleat to other eleaters. maybe in phase 2 or 3"*
 *
 * ⚠️ WHATSAPP AND EMAIL. NO SMS — *"too expensive and not necessary."*
 * ⚠️ ELEATER-TO-ELEATER SHARING IS PHASE 2 OR 3 and appears here in NO form.
 *    Not a row, not a greyed-out row — a disabled control is a promise.
 * ⚠️ SHARING IS NOT GATED. It asks nothing about who the Eleater is and it
 *    brings somebody who has never heard of Eleat. An account in front of it
 *    would be a toll on our own marketing.
 * ⚠️ IT IS A BUTTON, NEVER A POPUP. Nothing opens this by itself.
 * ⚠️ WHAT TRAVELS IS THE RESTAURANT'S OWN WORDS AND A LINK — never a line of
 *    Eleat marketing sitting in somebody's private chat.
 *
 * ── WHY IT IS PLAIN DOM AND NOT A REACT COMPONENT ──────────────────────────
 *
 * It is one overlay, opened from four different screens, holding no state
 * React needs to see. A component would have to be mounted by every screen
 * that can share, and the fourth screen to forget is the one nobody notices.
 * This is the locked file's own structure, so the two cannot drift.
 *
 * ⚠️ AND NO `var(--cream, #F5E4D2)` ANYWHERE. The locked file says why in
 * capitals: the token AND a copy of its hex is a second palette (RULE ZERO-A).
 * `#1B140D`, `#F5E4D2` and `#FF5C34` below are quoted from `eleat_share.js`
 * verbatim, because this sheet is appended to `document.body` and cannot rely
 * on a screen's own scope having declared them.
 * ══════════════════════════════════════════════════════════════════════════ */

/* the paper plane, quoted from `08a_Saved.html SHARE_ICON` — and the same
   path `11_Restaurant.html` uses for the hero button and the Feed post, so
   there is one share glyph in Eleat and not three. */
export const SHARE_PATHS = [
  'M21.5 2.5L2.8 9.9l7.3 2.6 2.6 7.3 8.8-17.3z',
  'M10.1 12.5l4.2-4.2',
];

const CSS = [
  '.esh-wrap{position:fixed;inset:0;z-index:9700;display:none}',
  '.esh-wrap.on{display:block}',
  '.esh-scrim{position:absolute;inset:0;background:rgba(21,15,10,.62)}',
  /* ⚠️ 44px, which the locked file's own comment calls "the smallest a finger
     can be asked to hit" — and which that file had set to 34 until it was
     caught. Copied at 44. */
  '.esh-x{position:absolute;top:8px;right:10px;width:44px;height:44px;',
  'border-radius:50%;border:1px solid rgba(245,228,210,.18);',
  'background:transparent;color:#F5E4D2;font-size:22px;line-height:1;',
  'cursor:pointer;display:grid;place-items:center;font-family:inherit;padding:0}',
  '.esh-x:focus-visible{outline:2px solid #FF5C34;outline-offset:2px}',
  '.esh{position:absolute;left:0;right:0;bottom:0;max-width:430px;margin:0 auto;',
  'background:#1B140D;border-top-left-radius:22px;border-top-right-radius:22px;',
  'border:1px solid rgba(245,228,210,.16);border-bottom:0;',
  'padding:8px 16px max(20px,env(safe-area-inset-bottom));',
  'font-family:Montserrat,-apple-system,sans-serif;color:#F5E4D2}',
  '.esh-grip{width:38px;height:4px;border-radius:2px;background:rgba(245,228,210,.22);',
  'margin:6px auto 14px}',
  '.esh-t{font-size:15px;font-weight:600;margin:0 2px 3px}',
  '.esh-s{font-size:12.5px;color:rgba(245,228,210,.62);margin:0 2px 16px;line-height:1.5;',
  'display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden}',
  '.esh-row{display:flex;gap:10px;margin-bottom:10px}',
  '.esh-b{flex:1;display:flex;align-items:center;justify-content:center;gap:9px;',
  'background:rgba(245,228,210,.06);border:1px solid rgba(245,228,210,.16);',
  'border-radius:14px;padding:14px 10px;font:600 13.5px Montserrat,sans-serif;',
  'color:#F5E4D2;cursor:pointer;text-decoration:none}',
  '.esh-b:active{background:rgba(245,228,210,.11)}',
  '.esh-b svg{flex:none}',
  '.esh-b.wa svg{fill:#25D366}',
  '.esh-copy{width:100%;background:none;border:0;padding:13px;',
  'color:rgba(245,228,210,.62);font:500 12.5px Montserrat,sans-serif;cursor:pointer}',
  '.esh-done{color:#FF5C34}',
].join('');

const WA = '<svg width="21" height="21" viewBox="0 0 24 24" fill="currentColor">'
  + '<path d="M17.47 14.38c-.3-.15-1.76-.87-2.03-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.94 1.16-.17.2-.35.22-.64.08-.3-.15-1.26-.46-2.39-1.48-.88-.79-1.48-1.76-1.65-2.06-.17-.3-.02-.46.13-.6.13-.14.3-.35.45-.52.15-.18.2-.3.3-.5.1-.2.05-.37-.03-.52-.07-.15-.67-1.61-.91-2.2-.25-.58-.49-.5-.67-.51h-.57c-.2 0-.52.07-.8.37-.27.3-1.04 1.02-1.04 2.48s1.07 2.87 1.22 3.07c.15.2 2.1 3.2 5.08 4.49.7.3 1.26.49 1.69.62.71.23 1.36.2 1.87.12.57-.09 1.76-.72 2-1.41.25-.7.25-1.29.18-1.42-.08-.12-.28-.2-.58-.35m-5.42 7.4a9.87 9.87 0 01-5.03-1.38l-.36-.21-3.74.98 1-3.65-.24-.37a9.86 9.86 0 01-1.51-5.26c0-5.45 4.44-9.89 9.89-9.89 2.64 0 5.12 1.03 6.99 2.9a9.83 9.83 0 012.89 6.99c0 5.45-4.44 9.89-9.89 9.89m8.41-18.3A11.82 11.82 0 0012.05 0C5.5 0 .16 5.34.16 11.89c0 2.1.55 4.14 1.59 5.95L.06 24l6.3-1.65a11.88 11.88 0 005.69 1.45c6.55 0 11.89-5.34 11.89-11.89 0-3.18-1.24-6.17-3.48-8.42Z"/></svg>';

const MAIL = '<svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" '
  + 'stroke-width="1.8" stroke-linejoin="round"><rect x="2.5" y="4.5" width="19" height="15" rx="2.5"/>'
  + '<path d="M3 6.5l9 6.5 9-6.5"/></svg>';

/* ══════════════════════════════════════════════════════════════════════════
 * ⚠️⚠️ A SHARE IS AN EVENT, AND THERE HAS BEEN A TABLE FOR IT SINCE
 * MIGRATION 061 THAT NO SCREEN HAS EVER WRITTEN TO.
 *
 * `POST /restaurants/:id/share` exists, and so does the restaurant's
 * `GET /:id/shares` which reads counts out of a view. Al, 10 September 2026:
 * share is *"tracked — needs an account and a table."*  The table is there.
 * Nothing in `web/src` had ever called it, so every share ever made counted
 * as zero — and the restaurant-facing number those counts feed is a sales
 * conversation.
 *
 * ⚠️ AND THE TWO RULES ARE NOT IN CONFLICT, WHICH IS WORTH SAYING OUT LOUD.
 * `eleat_share.js` says sharing is *"deliberately absent from eleat_gate.js"*;
 * the route says `requireAuth`. **The BUTTON is not gated and the RECORD is.**
 * So the sheet opens for anybody, signed in or not — an account in front of it
 * would be a toll on Eleat's own marketing — and the event is written only
 * when there is somebody to attribute it to. A failed or skipped record never
 * interferes with the share itself.
 *
 * ⚠️⚠️ AND EMAIL IS COUNTED — FIXED 26 SEP 2026, AND IT WAS NEVER A MIGRATION.
 * `shareBody.channel` listed `whatsapp`, `copy_link` and `system` and **not
 * `email`**, so every email share was uncountable while the sheet had offered
 * Email since 29 August. I reported that to Al as a schema change needing his
 * approval. **It was one line in a zod validator**: migration 061 makes
 * `channel` a plain `text` column with no enum and no CHECK, deliberately.
 * Reading 061 rather than assuming settled it in one command — RULE ZERO, and
 * it cost him a decision he should never have been asked for.
 * ══════════════════════════════════════════════════════════════════════════ */
let record = null;   /* set by openShare, read by the channel handlers */

function recordShare(channel) {
  if (!record || !record.venueId || !channel) return;
  try {
    /* imported lazily: `api.js` imports nothing from here, and a static import
       would pull the whole API layer into any screen that only draws a button */
    import('./api.js').then(({ call, getToken }) => {
      if (!getToken()) return;          /* not signed in — the button still worked */
      call(`/restaurants/${encodeURIComponent(record.venueId)}/share`, {
        method: 'POST',
        auth: true,
        body: {
          subjectType: record.subjectType || 'restaurant',
          subjectId: record.subjectId || null,
          channel,
        },
      }).catch(() => { /* a lost count never costs an Eleater their share */ });
    }).catch(() => {});
  } catch { /* as above */ }
}

let wrap = null;
let returnTo = null;

function style() {
  if (document.getElementById('eleat-share-css')) return;
  const s = document.createElement('style');
  s.id = 'eleat-share-css';
  s.textContent = CSS;
  (document.head || document.documentElement).appendChild(s);
}

export function closeShare() {
  if (wrap) wrap.classList.remove('on');
  /* ⚠️ FOCUS COMES BACK TO WHATEVER OPENED IT, or a keyboard is left at the
     top of the page with no idea where it is. */
  try { if (returnTo && returnTo.focus) returnTo.focus(); } catch { /* gone */ }
  returnTo = null;
}

function build() {
  if (wrap) return wrap;
  style();
  wrap = document.createElement('div');
  wrap.className = 'esh-wrap';
  wrap.innerHTML = '<div class="esh-scrim" data-close></div>'
    + '<div class="esh" role="dialog" aria-modal="true" aria-label="Share">'
    + '<div class="esh-grip"></div>'
    /* ⚠️ AN X WHERE A PERSON LOOKS FIRST. In the locked file the only way out
       was tapping the dimmed area, which nothing on screen mentioned. */
    + '<button class="esh-x" id="eshX" type="button" aria-label="Close">&times;</button>'
    + '<div class="esh-t" id="eshT"></div>'
    + '<div class="esh-s" id="eshS"></div>'
    + '<div class="esh-row">'
    + '<a class="esh-b wa" id="eshWa" target="_blank" rel="noopener">' + WA + 'WhatsApp</a>'
    + '<a class="esh-b" id="eshMail">' + MAIL + 'Email</a>'
    + '</div>'
    + '<button class="esh-copy" id="eshCopy" type="button">Copy link</button>'
    + '</div>';
  document.body.appendChild(wrap);

  wrap.addEventListener('click', (e) => {
    if (e.target && e.target.getAttribute
      && e.target.getAttribute('data-close') !== null) closeShare();
  });
  wrap.querySelector('#eshX').addEventListener('click', closeShare);

  /* ⚠️ ESCAPE, because it says role="dialog" and that is a promise. */
  document.addEventListener('keydown', (e) => {
    if (!wrap || !wrap.classList.contains('on')) return;
    if (e.key === 'Escape') { e.preventDefault(); closeShare(); }
  });

  /* ⚠️ AND IT DRAGS DOWN. The grip is drawn as a handle, and a handle that
     does nothing when pulled is a small lie the whole sheet pays for. The
     gesture belongs to the WHOLE sheet, not the 4px grip — binding it to the
     grip alone asks for a four-pixel target. */
  const sheet = wrap.querySelector('.esh');
  let y0 = null;
  sheet.addEventListener('touchstart', (e) => {
    y0 = e.touches && e.touches[0] ? e.touches[0].clientY : null;
  }, { passive: true });
  sheet.addEventListener('touchmove', (e) => {
    if (y0 === null) return;
    const y = e.touches && e.touches[0] ? e.touches[0].clientY : y0;
    if (y - y0 > 46) { y0 = null; closeShare(); }
  }, { passive: true });
  sheet.addEventListener('touchend', () => { y0 = null; }, { passive: true });

  return wrap;
}

/**
 * Open the sheet.
 *
 * @param title what it is — the restaurant's name, never "Shared from Eleat".
 * @param text  the restaurant's own words about the thing being shared.
 * @param url   defaults to the page they are on, which is the thing itself.
 */
export function openShare({
  title = 'Eleat', text = '', url = null,
  venueId = null, subjectType = 'restaurant', subjectId = null,
} = {}) {
  /* ⚠️ SHARING NEEDS AN ACCOUNT NOW — Al, 6 Oct 2026: "i can click the share
     button without it asking me to sign in… you have this backward and should
     ask to sign in then become an eleater". This replaces the earlier rule
     that kept Share open to visitors. */
  if (needsAccount('share')) return;
  build();
  record = venueId ? { venueId, subjectType, subjectId } : null;
  try { returnTo = document.activeElement; } catch { returnTo = null; }

  const link = String(url || window.location.href);
  const body = text ? `${text}\n\n${link}` : link;

  document.getElementById('eshT').textContent = String(title);
  document.getElementById('eshS').textContent = text || link;

  const wa = document.getElementById('eshWa');
  wa.setAttribute('href', 'https://wa.me/?text=' + encodeURIComponent(body));
  /* ⚠️ RECORDED ON THE TAP, NOT ON OPENING THE SHEET. Opening it is not
     sharing — the locked file's own point that this is a button and never a
     popup cuts both ways: somebody who opens the sheet and closes it again has
     shared nothing, and counting that would inflate every number. */
  wa.onclick = () => recordShare('whatsapp');

  /* ⚠️ THE SUBJECT SAYS WHAT IT IS. An email headed "Shared from Eleat" tells
     the person nothing; the name of the restaurant tells them whether to
     open it. */
  const mail = document.getElementById('eshMail');
  mail.setAttribute('href',
    'mailto:?subject=' + encodeURIComponent(String(title))
    + '&body=' + encodeURIComponent(body));
  /* ⚠️ COUNTED NOW. Al, 26 Sep 2026: *"You can just email it to somebody, and
     it's there. So leave it like that and do that."*  The route's validator was
     the only thing refusing `email` — migration 061's column has no enum. */
  mail.onclick = () => recordShare('email');

  const copy = document.getElementById('eshCopy');
  copy.textContent = 'Copy link';
  copy.className = 'esh-copy';
  copy.onclick = () => {
    try {
      if (navigator.clipboard) navigator.clipboard.writeText(link);
      copy.textContent = 'Link copied';
      copy.className = 'esh-copy esh-done';
      recordShare('copy_link');
    } catch { /* an unavailable clipboard is not worth a message */ }
  };

  wrap.classList.add('on');
}

/* ══════════════════════════════════════════════════════════════════════════
 * WHAT TRAVELS, PER THING — ALL FOUR QUOTED, NONE WRITTEN HERE
 *
 * ⚠️ THESE LIVE IN ONE PLACE FOR THE REASON `brief()` AND THE LANGUAGE LINE
 * DO: four screens composing their own sentence is four sentences that drift,
 * and the drift is invisible because it only shows up in somebody else's
 * WhatsApp.
 * ══════════════════════════════════════════════════════════════════════════ */

/** `11_Restaurant.html #shareVenue` — the hero button, beside Save. */
export function shareVenue(v) {
  const name = (v && v.name) || '';
  return { title: `${name} on Eleat`, text: `${name} — worth a look on Eleat.` };
}

/** `08a_Saved.html` — the card's own `.sharebtn`. */
export function shareSaved(v) {
  const name = (v && v.name) || '';
  return { title: `${name} on Eleat`, text: `${name} — saved on Eleat.` };
}

/** `11_Restaurant.html #fvShare` — a Feed post. The CAPTION leads, because
 *  a picture shared with no reason attached is just a picture. */
export function shareFeedPost(post, venueName) {
  const cap = (post && (post.caption || post.body)) || '';
  return {
    title: venueName || 'Eleat',
    text: `${cap} — ${venueName || 'Eleat'} on Eleat`,
  };
}

/** `11_Restaurant.html #shShare` — one dish out of a menu, with its price. */
export function shareItem(item, venueName) {
  const n = (item && item.name) || '';
  const p = item && item.price != null ? `$${item.price}` : '';
  return {
    title: `${venueName || 'Eleat'} on Eleat`,
    text: `${n}${p ? ` · ${p}` : ''} — at ${venueName || 'Eleat'} on Eleat.`,
  };
}
